import { useMemo } from "react";
import type { CameraApiResponse } from "@src/services/api/cameras-api";
import type { SortSelection } from "@src/features/clips/types/camera";
import { filterCamerasByQuery } from "@src/features/cameras/utils/camera-filter";
import { sortCameras } from "@src/features/cameras/utils/camera-sort";
import { groupCamerasBySite } from "@src/features/cameras/utils/camera-group";

interface UseCameraCollectionParams {
  cameras?: CameraApiResponse[];
  searchQuery: string;
  sortBy: SortSelection;
}

export function useCameraCollection({ cameras, searchQuery, sortBy }: UseCameraCollectionParams) {
  const filteredCameras = useMemo(() => {
    if (!cameras) return [];
    return filterCamerasByQuery(cameras, searchQuery);
  }, [cameras, searchQuery]);

  const sortedCameras = useMemo(() => {
    if (filteredCameras.length === 0) return [];
    if (sortBy.length === 0) return filteredCameras;
    return sortCameras(filteredCameras, sortBy);
  }, [filteredCameras, sortBy]);

  const groupedCameras = useMemo(() => {
    if (filteredCameras.length === 0) return [];
    const normalizedSort = sortBy.length === 0 ? "id" : sortBy;
    return groupCamerasBySite(filteredCameras, normalizedSort).map((group) => ({
      ...group,
      thumbnailUrl: group.cameras[0]?.snapshot?.url ?? null,
    }));
  }, [filteredCameras, sortBy]);

  return {
    filteredCameras,
    sortedCameras,
    groupedCameras,
    hasData: cameras !== undefined && cameras.length > 0,
  };
}
